import React from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import MerchantDashboard from "./pages/MerchantDashboard";
import CustomerWallet from "./pages/CustomerWallet";
import AdminDashboard from "./pages/AdminDashboard";
import RoleSelect from "./pages/RoleSelect";
import DeleteAccount from "./pages/DeleteAccount";
import ResetPassword from "./pages/ResetPassword";
import LegalPage from "./pages/LegalPage";
import AboutPage from "./pages/AboutPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import ForMerchantsPage from "./pages/ForMerchantsPage";
import FaqPage from "./pages/FaqPage";
import ContactPage from "./pages/ContactPage";
import LiveBoardPage from "./pages/LiveBoardPage";
import { CookieConsent } from "./components/CookieConsent";
import "./App.css";

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
    </div>
  );
}

function Dashboard() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded === false && user.role !== "admin" && user.role !== "super_admin")
    return <RoleSelect />;
  switch (user.role) {
    case "merchant": return <MerchantDashboard />;
    case "admin": return <AdminDashboard />;
    case "super_admin": return <AdminDashboard />;
    default: return <CustomerWallet />;
  }
}

function MerchantReferral() {
  const { code } = useParams();
  React.useEffect(() => {
    if (code) localStorage.setItem("paras_referral_code", code.trim().toUpperCase());
  }, [code]);
  return <Navigate to="/dashboard" replace />;
}

function AppRouter() {
  const location = useLocation();
  // Auto-save a referral code from ?ref= / ?referral= on ANY page (persists in localStorage
  // with no expiry) so the attribution survives sign-up, app restarts and deep links.
  React.useEffect(() => {
    const p = new URLSearchParams(location.search);
    const ref = p.get("ref") || p.get("referral") || p.get("ref_code");
    if (ref) localStorage.setItem("paras_referral_code", ref.trim().toUpperCase());
  }, [location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/account-deletion" element={<DeleteAccount />} />
      <Route path="/privacy" element={<LegalPage slug="privacy" />} />
      <Route path="/cookie-policy" element={<LegalPage slug="cookie" />} />      <Route path="/terms" element={<LegalPage slug="terms" />} />
      <Route path="/refund-policy" element={<LegalPage slug="refund" />} />
      <Route path="/cashback-rules" element={<LegalPage slug="cashback" />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/live" element={<LiveBoardPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/for-merchants" element={<ForMerchantsPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/merchant-referral/:code" element={<MerchantReferral />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/admin" element={<Dashboard />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App dark">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <CookieConsent />
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}
